import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { NavTab, BottomNavbar } from './BottomNavbar';
import { CabinetHeader } from './CabinetHeader';
import { BurgerDrawer } from './BurgerDrawer';
import { IosPushBanner } from '../ui/IosPushBanner';
import { CabinetFooter } from './CabinetFooter';
import { DesktopSidebar } from './DesktopSidebar';
import { CabinetTour } from '../tour/CabinetTour';
import { useTour, shouldShowTour } from '../../hooks/useTour';
import { PushPromptModal } from '../ui/PushPromptModal';

// Pages
import { ConsultationsPage } from '../../pages/ConsultationsPage';
import { TherapyPage } from '../../pages/TherapyPage';
import { ServicesPage } from '../../pages/ServicesPage';
import { ServicesPageBC } from '../../pages/ServicesPageBC';
import { DocumentsPage } from '../../pages/DocumentsPage';
import { ProfilePage } from '../../pages/ProfilePage';
import { VisitsPage } from '../../pages/VisitsPage';
import { PwaInstallBanner } from '../profile/PwaInstallBanner';
import { WelcomeCouponBanner } from '../profile/WelcomeCouponBanner';
import { VisitSummaryPage } from '../../pages/VisitSummaryPage';
import { DietSurveyPage } from '../../pages/DietSurveyPage';
import PostponementApp from '../../../PostponementApp';
import { getUrlParam } from '../../utils/url';

const mapUrlToTab = (urlTab: string | null): NavTab => {
  switch (urlTab) {
    case 'moja-terapia':
    case 'therapy':
      return 'therapy';
    case 'moje-wizyty':
    case 'visits':
      return 'visits';
    case 'uslugi':
    case 'services':
      return 'services';
    case 'dokumenty':
    case 'documents':
      return 'documents';
    case 'moje-dane':
    case 'profile':
      return 'profile';
    case 'przeloz':
    case 'postponement':
      return getUrlParam('hash') !== null ? 'przeloz' : 'consultations';
    case 'visit-summary':
      return 'visit-summary';
    case 'diet_survey':
      return 'diet_survey';
    case 'wizyty':
    case 'consultations':
    default:
      return 'consultations';
  }
};

const mapTabToUrl = (tab: NavTab): string => {
  switch (tab) {
    case 'therapy':
      return 'moja-terapia';
    case 'visits':
      return 'moje-wizyty';
    case 'services':
      return 'uslugi';
    case 'documents':
      return 'dokumenty';
    case 'profile':
      return 'moje-dane';
    case 'przeloz':
      return 'przeloz';
    case 'visit-summary':
      return 'visit-summary';
    case 'diet_survey':
      return 'diet_survey';
    case 'consultations':
    default:
      return 'wizyty';
  }
};

export const CabinetShell: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTabState] = useState<NavTab>(() => {
    const urlTab = getUrlParam('tab');
    if (urlTab) return mapUrlToTab(urlTab);
    
    if (user) {
      const isPersonaA = user.personaType === 'A' || user.isWeightLoss === true;
      return isPersonaA ? 'therapy' : 'consultations';
    }
    
    return 'consultations';
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Onboarding tour
  const tour = useTour(user?.personaType ?? 'C', user?.isWeightLoss);

  const setActiveTab = (tab: NavTab) => {
    setActiveTabState(tab);
    
    // Sync tab parameter with URL search query
    const params = new URLSearchParams(window.location.search);
    
    // Clear postponement parameters if we switch to another tab
    if (tab !== 'przeloz') {
      params.delete('order_id');
      params.delete('hash');
    }
    
    params.set('tab', mapTabToUrl(tab));
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({ path: newUrl }, '', newUrl);
  };

  // Sync back/forward browser navigation
  useEffect(() => {
    const handlePopState = () => {
      setActiveTabState(mapUrlToTab(getUrlParam('tab')));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Set default tab if no tab parameter in URL, based on persona
  useEffect(() => {
    if (!getUrlParam('tab') && user) {
      const isPersonaA = user.personaType === 'A' || user.isWeightLoss === true;
      setActiveTab(isPersonaA ? 'therapy' : 'consultations');
    }
  }, [user]);

  // Якщо user не є Persona A і не є в програмі схуднення, але відкрита вкладка therapy — редиректуємо
  useEffect(() => {
    const isPersonaA = user && (user.personaType === 'A' || user.isWeightLoss === true);
    if (user && !isPersonaA && activeTab === 'therapy') {
      setActiveTab('services');
    }
  }, [user, activeTab]);

  // Prefetch data in background on initial load
  useEffect(() => {
    if (user && activeTab !== 'przeloz') {
      // Import on demand to avoid circular dependencies if any
      import('../../hooks/useQuery').then(({ prefetchQuery }) => {
        import('../../api/cabinetApi').then(({ getConsultations, getDocuments }) => {
          prefetchQuery('consultations', getConsultations).catch(() => {});
          prefetchQuery('documents', getDocuments).catch(() => {});
        });
      });
    }
  }, [user, activeTab]);

  // Auto-start tour on first load (when user is loaded and tour wasn't seen yet)
  useEffect(() => {
    if (!user) return;
    if (shouldShowTour()) {
      const timer = setTimeout(() => tour.startTour(), 600);
      return () => clearTimeout(timer);
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTourEnd = () => {
    tour.endTour();
    if (user) {
      const isPersonaA = user.personaType === 'A' || user.isWeightLoss === true;
      setActiveTab(isPersonaA ? 'therapy' : 'consultations');
    }
  };

  console.log(user, "user !");
  

  if (!user && activeTab !== 'przeloz') return null; // Should be handled by AuthGuard if not public postponement

  const renderContent = () => {
    switch (activeTab) {
      case 'consultations':
        return <ConsultationsPage onNavigateToServices={() => setActiveTab('services')} />;
      case 'therapy':
        return <TherapyPage onNavigateToVisits={() => setActiveTab('visits')} />;
      case 'visits':
        return <VisitsPage onBack={() => setActiveTab('therapy')} />;
      case 'services': {
        const isPersonaA = user && (user.personaType === 'A' || user.isWeightLoss === true);
        return isPersonaA ? <ServicesPage /> : <ServicesPageBC />;
      }
      case 'documents':
        return <DocumentsPage />;
      case 'profile':
        return <ProfilePage />;
      case 'przeloz':
        return <PostponementApp />;
      case 'diet_survey':
        return <DietSurveyPage onComplete={() => setActiveTab('therapy')} />;
      case 'visit-summary':
        return <VisitSummaryPage onBack={() => {
          const isPersonaA = user && (user.personaType === 'A' || user.isWeightLoss === true);
          setActiveTab(isPersonaA ? 'visits' : 'consultations');
        }} />;
      default:
        return null;
    }
  };

  return (
    <div className="cab-screen-wrap">
      <CabinetHeader />
      
      <div className="cab-desktop-layout">
        {activeTab !== 'przeloz' && activeTab !== 'diet_survey' && user && (
          <DesktopSidebar 
            activeTab={activeTab} 
            onChangeTab={setActiveTab} 
            persona={user.personaType}
            isWeightLoss={user.isWeightLoss}
          />
        )}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {activeTab !== 'documents' && activeTab !== 'przeloz' && activeTab !== 'diet_survey' && user && (
            <PwaInstallBanner className={activeTab === 'profile' ? 'hide-on-mobile' : 'mobile-pt'} />
          )}
          {(activeTab === 'consultations' || activeTab === 'services' || activeTab === 'therapy') && user && (
            <WelcomeCouponBanner className="mobile-pt" />
          )}
          <div className="cab-content">
            {/* {user && <IosPushBanner />} */}
            {renderContent()}
          </div>
        </div>
      </div>

      {/* <CabinetFooter /> */}

      {activeTab !== 'przeloz' && activeTab !== 'diet_survey' && user && (
        <div className="cab-mobile-nav">
          <BottomNavbar 
            activeTab={activeTab} 
            onChangeTab={setActiveTab} 
            persona={user.personaType}
            isWeightLoss={user.isWeightLoss}
          />

          <BurgerDrawer 
            isOpen={isDrawerOpen} 
            onClose={() => setIsDrawerOpen(false)} 
            persona={user.personaType}
            isWeightLoss={user.isWeightLoss}
            activeTab={activeTab}
            onChangeTab={setActiveTab}
          />
        </div>
      )}

      {/* Onboarding tour — renders above everything */}
      {user && activeTab !== 'przeloz' && activeTab !== 'diet_survey' && (
        <CabinetTour
          isActive={tour.isActive}
          currentStep={tour.currentStep}
          steps={tour.steps}
          totalSteps={tour.totalSteps}
          activeTab={activeTab}
          onChangeTab={setActiveTab}
          onNext={tour.goNext}
          onPrev={tour.goPrev}
          onEnd={handleTourEnd}
        />
      )}

      {user && activeTab !== 'przeloz' && activeTab !== 'diet_survey' && (
        <PushPromptModal />
      )}
    </div>
  );
};
